import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Share2 } from 'lucide-react';
import EventFinderAPI from '../services/api';
import type { Event, EventGroup, EventGroupEventsResponse, EventGroupMapLocation, Pagination } from '../types/api';
import { SEO } from '../components/SEO';
import EventsExplorer, { ExplorerControlButton } from '../components/events/EventsExplorer';
import { copyToClipboard } from '../utils/eventCalendar';
import { useAnalytics } from '../hooks/useAnalytics';
import { colors, spacing, typography, borderRadius, zIndex } from '../styles/theme';

const PAGE_SIZE = 20;
const FILTER_KEYS = ['days', 'from', 'to', 'format', 'rel', 'tod', 'vibe', 'hideEntity'];

const Breadcrumbs = styled.nav`
  margin-bottom: ${spacing[3]};
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSizes.sm};

  a {
    color: ${colors.text.link};
    text-decoration: none;
  }
`;

const MemberNames = styled.span`
  display: block;
  max-width: 960px;
  overflow-wrap: anywhere;
  line-height: ${typography.lineHeights.relaxed};
`;

const Toast = styled.div`
  position: fixed;
  z-index: ${zIndex.toast};
  right: ${spacing[4]};
  bottom: ${spacing[4]};
  max-width: min(360px, calc(100vw - ${spacing[8]}));
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borderRadius.md};
  background: ${colors.gray[900]};
  color: ${colors.white};
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.24);
`;

function apiFilters(searchParams: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = searchParams.get(key);
    if (value) params.set(key, value);
  }
  return params;
}

function requestParams(filters: URLSearchParams, scope: 'upcoming' | 'recent' | 'all', page = 1): URLSearchParams {
  const params = new URLSearchParams(filters);
  params.set('scope', scope);
  params.set('page', String(page));
  params.set('limit', String(PAGE_SIZE));
  return params;
}

function appendUnique(current: Event[], next: Event[]): Event[] {
  const seen = new Set(current.map(event => event.eventId));
  return [...current, ...next.filter(event => !seen.has(event.eventId))];
}

function mapLocationEvents(location: EventGroupMapLocation): Event[] {
  return location.events.map(event => ({
    ...event,
    city: location.city || undefined,
    state: location.state || undefined,
    country: location.country || undefined,
    coordinates: location.coordinates,
    entities: [],
  }));
}

export const EventGroupPage: React.FC = () => {
  const { groupSlug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { trackEvent } = useAnalytics();
  const filters = useMemo(() => apiFilters(searchParams), [searchParams]);
  const filterKey = filters.toString();
  const activeFilterKey = useRef(filterKey);

  const [group, setGroup] = useState<EventGroup | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [recentEvents, setRecentEvents] = useState<Event[]>([]);
  const [upcomingPagination, setUpcomingPagination] = useState<Pagination | null>(null);
  const [recentPagination, setRecentPagination] = useState<Pagination | null>(null);
  const [vibeOptions, setVibeOptions] = useState<string[]>([]);
  const [mapEvents, setMapEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    activeFilterKey.current = filterKey;
    const controller = new AbortController();
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setNotFound(false);
    setUpcomingEvents([]);
    setRecentEvents([]);
    setMapEvents([]);
    setUpcomingPagination(null);
    setRecentPagination(null);

    (async () => {
      try {
        const resolved = await EventFinderAPI.getEventGroup(groupSlug, controller.signal);
        if (cancelled) return;
        if (!resolved) {
          setNotFound(true);
          setIsLoading(false);
          return;
        }
        if (resolved.redirectTo && resolved.redirectTo !== window.location.pathname) {
          navigate({ pathname: resolved.redirectTo, search: searchParams.toString() }, { replace: true });
          return;
        }
        setGroup(resolved);

        const [upcoming, recent, map] = await Promise.all([
          EventFinderAPI.getEventGroupEvents(groupSlug, requestParams(filters, 'upcoming'), controller.signal),
          EventFinderAPI.getEventGroupEvents(groupSlug, requestParams(filters, 'recent'), controller.signal),
          EventFinderAPI.getEventGroupMap(groupSlug, requestParams(filters, 'all'), controller.signal),
        ]);
        if (cancelled) return;
        setUpcomingEvents(upcoming.events);
        setRecentEvents(recent.events);
        setUpcomingPagination(upcoming.pagination);
        setRecentPagination(recent.pagination);
        setVibeOptions(Array.from(new Set([...upcoming.facets.vibes, ...recent.facets.vibes])).sort());
        setMapEvents(map.locations.flatMap(mapLocationEvents));
        setIsLoading(false);
      } catch (loadError) {
        if (cancelled || (loadError as Error).name === 'AbortError') return;
        setError((loadError as Error).message || 'The Event Group could not be loaded.');
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `filterKey` deliberately excludes `view`, so list/grid changes do not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupSlug, filterKey, reloadToken, navigate]);

  useEffect(() => {
    const creationOutcome = (location.state as { creationOutcome?: string } | null)?.creationOutcome;
    if (creationOutcome) setToast(creationOutcome === 'created' ? 'Event Group created. Share the link when ready.' : 'This group already existed. Its link is ready to share.');
  }, [location.state]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const loadMore = useCallback(async (scope: 'upcoming' | 'recent') => {
    const pagination = scope === 'upcoming' ? upcomingPagination : recentPagination;
    if (!pagination?.hasNextPage) return;
    const requestFilterKey = activeFilterKey.current;
    if (scope === 'upcoming') setLoadingUpcoming(true);
    else setLoadingRecent(true);
    try {
      const result: EventGroupEventsResponse = await EventFinderAPI.getEventGroupEvents(
        groupSlug,
        requestParams(apiFilters(new URLSearchParams(requestFilterKey)), scope, pagination.page + 1),
      );
      if (activeFilterKey.current !== requestFilterKey) return;
      if (scope === 'upcoming') {
        setUpcomingEvents(current => appendUnique(current, result.events));
        setUpcomingPagination(result.pagination);
      } else {
        setRecentEvents(current => appendUnique(current, result.events));
        setRecentPagination(result.pagination);
      }
      trackEvent({ action: 'load_more', category: 'event_group_events', label: scope, value: result.events.length, custom_parameters: { action_location: 'event_list', scope } });
    } catch (loadError) {
      setToast((loadError as Error).message || 'More events could not be loaded.');
    } finally {
      if (scope === 'upcoming') setLoadingUpcoming(false);
      else setLoadingRecent(false);
    }
  }, [groupSlug, recentPagination, trackEvent, upcomingPagination]);

  const handleShare = useCallback(async () => {
    if (!group) return;
    const url = new URL(group.canonicalUrl, window.location.origin).href;
    let outcome = 'failed';
    const share = (navigator as Navigator & { share?: (data: ShareData) => Promise<void> }).share;
    if (typeof share === 'function') {
      try {
        await share({ title: 'Event Group', url });
        outcome = 'shared';
      } catch (shareError) {
        if (!(shareError instanceof DOMException && shareError.name === 'AbortError')) {
          outcome = await copyToClipboard(url) ? 'copied' : 'failed';
        }
      }
    } else {
      outcome = await copyToClipboard(url) ? 'copied' : 'failed';
    }
    setToast(outcome === 'shared' ? 'Event Group shared.' : outcome === 'copied' ? 'Link copied to clipboard.' : 'The link could not be shared.');
    trackEvent({ action: 'share_link', category: 'event_group', label: outcome, custom_parameters: { action_location: 'group_header', outcome } });
  }, [group, trackEvent]);

  const handleExport = useCallback((format: 'csv' | 'markdown' | 'text') => {
    if (!group) return;
    const anchor = document.createElement('a');
    anchor.href = EventFinderAPI.eventGroupExportUrl(group.slug, format, filters);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }, [filters, group]);

  if (!groupSlug) return <Navigate to="/" replace />;

  const allEvents = [...upcomingEvents, ...recentEvents];
  const upcomingTotal = upcomingPagination?.totalItems || 0;
  const recentTotal = recentPagination?.totalItems || 0;
  const total = upcomingTotal + recentTotal;
  const memberNames = group?.members.map(member => member.displayName).join(', ') || '';

  return (
    <>
      <SEO
        title="Event Group"
        description={group ? `${total} events from ${memberNames}` : undefined}
        noIndex
      />
      <EventsExplorer
        title="Event Group"
        subtitle={group ? <MemberNames>{memberNames}</MemberNames> : undefined}
        breadcrumbs={
          <Breadcrumbs aria-label="Breadcrumb">
            <a href="/">Home</a>
            {' / '}
            <span aria-current="page">Event Group</span>
          </Breadcrumbs>
        }
        events={allEvents}
        mapEvents={mapEvents}
        isLoading={isLoading}
        error={notFound ? 'This Event Group could not be found.' : error}
        onRetry={notFound ? undefined : () => setReloadToken(token => token + 1)}
        totalAvailable={total}
        resultTotal={total}
        upcomingTotal={upcomingTotal}
        recentTotal={recentTotal}
        exportLabel="Event Group"
        emptyMessage="There are no upcoming or recent events for this group yet."
        telemetryCategory="event_group_events"
        remoteFiltering
        vibeOptionsOverride={vibeOptions}
        entityFilterOptions={group?.members.map(member => ({ value: member.entityId, label: member.displayName })) || []}
        hasMoreUpcoming={Boolean(upcomingPagination?.hasNextPage)}
        hasMoreRecent={Boolean(recentPagination?.hasNextPage)}
        isLoadingMoreUpcoming={loadingUpcoming}
        isLoadingMoreRecent={loadingRecent}
        onLoadMoreUpcoming={() => loadMore('upcoming')}
        onLoadMoreRecent={() => loadMore('recent')}
        onExportRequest={handleExport}
        toolbarExtra={group ? (
          <ExplorerControlButton type="button" onClick={handleShare}>
            <Share2 aria-hidden="true" size={16} />
            Share
          </ExplorerControlButton>
        ) : undefined}
      />
      {toast && <Toast role="status">{toast}</Toast>}
    </>
  );
};

export default EventGroupPage;